import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, LayoutGroup, motion, useInView, useReducedMotion } from "framer-motion";
import {
  CarFront, Cog, Gamepad2, Gauge, Gem, Pizza, Search, Shirt, ShoppingBag, Soup,
  Sandwich, Store, Trophy, UtensilsCrossed, Wrench,
} from "lucide-react";

const GRAD = "linear-gradient(135deg, #8b5cf6 0%, #3b82f6 50%, #ec4899 100%)";
const H = { fontFamily: "Outfit, sans-serif" };
const SCENES = [
  { key: "restaurant", icons: [UtensilsCrossed, Soup, Sandwich, Pizza], names: ["Le Comptoir Créole", "Burger House", "Pizza Mia"],
    ratings: [4.5, 4.4, 4.3], counts: [320, 180, 210], reviewer: "Julie R." },
  { key: "shop", icons: [ShoppingBag, Shirt, Gem, Store], names: ["Maison Lila", "Urban Store", "Atelier Coton"],
    ratings: [4.5, 4.4, 4.2], counts: [290, 205, 140], reviewer: "Sarah M." },
  { key: "garage", icons: [Wrench, CarFront, Gauge, Cog], names: ["Garage du Centre", "Méca Pro", "Auto Service Plus"],
    ratings: [4.5, 4.4, 4.3], counts: [260, 190, 150], reviewer: "Kevin T." },
];
const YOU = [{ rating: 4.1, count: 192 }, { rating: 4.3, count: 268 }, { rating: 4.4, count: 315 }, { rating: 4.6, count: 422 }];
const TINTS = ["bg-amber-50 text-amber-600", "bg-sky-50 text-sky-600", "bg-rose-50 text-rose-500"];
const PINS = [{ x: "10%", y: "60%" }, { x: "24%", y: "32%" }, { x: "38%", y: "66%" }, { x: "50%", y: "36%" }];
const DELAYS = [1700, 1800, 1800, 3400];
const SPRING = { type: "spring", stiffness: 210, damping: 28 };

function useCountUp(target, ms = 900, decimals = 0) {
  const [val, setVal] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    const start = performance.now();
    const a = from.current;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / ms);
      const v = a + (target - a) * (1 - Math.pow(1 - p, 3));
      setVal(v);
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return decimals ? val.toFixed(decimals) : Math.round(val);
}

function useTypewriter(text, speed = 28) {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    const id = setInterval(() => setN((x) => (x >= text.length ? (clearInterval(id), x) : x + 1)), speed);
    return () => clearInterval(id);
  }, [text, speed]);
  return text.slice(0, n);
}

function Stars({ rating }) {
  const row = "★★★★★";
  return (
    <span className="relative inline-block text-[11px] leading-none tracking-[1px] text-zinc-200" aria-hidden>
      {row}
      <span className="absolute inset-0 overflow-hidden text-amber-400 transition-[width] duration-700" style={{ width: `${(rating / 5) * 100}%` }}>
        {row}
      </span>
    </span>
  );
}

function SearchBar({ query, t }) {
  const typed = useTypewriter(query);
  return (
    <div className="flex items-center gap-2.5 rounded-full border border-black/[0.07] bg-white px-4 py-2.5 shadow-[0_2px_10px_rgba(15,23,42,0.05)]">
      <Search className="h-4 w-4 shrink-0 text-zinc-400" />
      <span className="flex-1 truncate text-[13.5px] text-zinc-700" data-testid="rank-anim-query">
        {typed}
        <span className="ml-px inline-block h-3.5 w-px translate-y-0.5 animate-pulse bg-zinc-400" />
      </span>
      <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[9.5px] font-extrabold uppercase tracking-[0.14em] text-violet-600">
        {t("lp.rank.example")}
      </span>
    </div>
  );
}

function MiniMap({ ranks }) {
  return (
    <div className="relative mt-3 h-[84px] overflow-hidden rounded-2xl bg-[#f3f4f6]">
      <div className="absolute -left-6 top-[46%] h-3 w-[120%] -rotate-6 bg-white/90" />
      <div className="absolute left-[38%] -top-4 h-[140%] w-3 rotate-12 bg-white/90" />
      <div className="absolute left-[72%] -top-4 h-[140%] w-2 -rotate-6 bg-white/80" />
      <div className="absolute -bottom-3 left-0 h-7 w-full bg-sky-100/80" />
      {ranks.map((r, i) => (
        <motion.div key={i} layout transition={SPRING} className="absolute -translate-x-1/2 -translate-y-full" style={{ left: PINS[i].x, top: PINS[i].y }}>
          <div
            className={`flex h-6 w-6 items-center justify-center rounded-full rounded-bl-none -rotate-45 shadow-md ${i === 0 ? "" : "bg-rose-500"}`}
            style={i === 0 ? { background: GRAD } : undefined}
          >
            <span className="rotate-45 text-[10px] font-extrabold text-white">{r}</span>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function ResultRow({ row, isYou, sep, t }) {
  const rating = useCountUp(row.rating, 900, 1);
  const count = useCountUp(row.count, 900);
  const Icon = row.icon;
  return (
    <motion.li
      layout
      transition={SPRING}
      className={`relative flex items-center gap-3 px-3.5 py-2.5 ${isYou ? "z-10 rounded-2xl bg-white shadow-[0_10px_30px_-10px_rgba(139,92,246,0.45)] ring-1 ring-violet-200" : ""}`}
      data-testid={isYou ? "rank-anim-you-row" : undefined}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-zinc-900">
          <span className="text-zinc-400 tabular-nums">{row.rank}.</span>
          <span className="truncate">{row.name}</span>
          {isYou && (
            <span className="rounded-full px-1.5 py-0.5 text-[9px] font-extrabold tracking-wider text-white" style={{ background: GRAD }}>
              {t("lp.rank.you")}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-zinc-500">
          <span className="font-semibold text-zinc-700 tabular-nums">{rating.replace(".", sep)}</span>
          <Stars rating={Number(rating)} />
          <span className="tabular-nums">({count})</span>
        </div>
        <div className="mt-0.5 truncate text-[11px] text-zinc-400">{row.meta}</div>
        <div className="text-[11px]"><span className="text-emerald-600">{row.hours}</span></div>
      </div>
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${isYou ? "text-white" : row.tint}`}
        style={isYou ? { background: GRAD } : undefined}
      >
        <Icon className="h-5 w-5" />
      </div>
    </motion.li>
  );
}

function ReviewToast({ show, scene, t }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key={show}
          initial={{ opacity: 0, y: 14, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.98 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="absolute right-2 top-[64px] z-20 w-[200px] rounded-2xl border border-black/[0.06] bg-white/95 p-3 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.28)] backdrop-blur-xl sm:-right-12 sm:top-[60px] sm:w-[220px]"
          data-testid="rank-anim-review"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ background: GRAD }}>
              {scene.reviewer[0]}
            </div>
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-zinc-900">{scene.reviewer}</div>
              <div className="text-[10px] leading-none text-amber-400">★★★★★</div>
            </div>
          </div>
          <p className="mt-2 hidden text-[11.5px] leading-snug text-zinc-600 sm:block">{t(`lp.rank.scenarios.${scene.key}.review`)}</p>
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[9.5px] font-bold text-violet-600">
            <Gamepad2 className="h-3 w-3" /> {t("lp.rank.via_game")}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function RankPill({ rank, t }) {
  const first = rank === 1;
  return (
    <motion.div
      layout
      className={`relative z-10 mx-auto mt-5 flex w-fit items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-bold shadow-[0_10px_30px_-10px_rgba(15,23,42,0.25)] ${first ? "text-white" : "border border-black/[0.07] bg-white text-zinc-800"}`}
      style={{ ...H, ...(first ? { background: GRAD } : {}) }}
      data-testid="rank-anim-pill"
    >
      {first && <Trophy className="h-4 w-4" />}
      <span>{first ? t("lp.rank.pill_first") : t("lp.rank.pill")}</span>
      {!first && (
        <span className="relative inline-flex h-5 min-w-[2.2rem] overflow-hidden">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={rank}
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="qrise-text-gradient"
            >
              {t(`lp.rank.ordinal.${rank}`)}
            </motion.span>
          </AnimatePresence>
        </span>
      )}
    </motion.div>
  );
}

function buildRows(scene, step, t) {
  const base = `lp.rank.scenarios.${scene.key}`;
  const hours = t(`${base}.hours`);
  const comps = scene.names.map((name, i) => ({
    id: `c${i}`, name, rating: scene.ratings[i], count: scene.counts[i], meta: t(`${base}.c.${i}`),
    icon: scene.icons[i + 1], tint: TINTS[i], hours,
  }));
  const you = { id: "you", name: t("lp.rank.your_business"), ...YOU[step], meta: t(`${base}.you`), icon: scene.icons[0], hours };
  const rows = [...comps];
  rows.splice(3 - step, 0, you);
  return rows.map((r, i) => ({ ...r, rank: i + 1 }));
}

export default function RankAnimation() {
  const { t, i18n } = useTranslation();
  const sep = i18n.language?.startsWith("fr") ? "," : ".";
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3 });
  const reduce = useReducedMotion();
  const [sceneIdx, setSceneIdx] = useState(0);
  const [step, setStep] = useState(0);
  const cur = reduce ? 3 : step;

  useEffect(() => {
    if (!inView || reduce) return undefined;
    const id = setTimeout(() => {
      if (step < 3) setStep(step + 1);
      else { setStep(0); setSceneIdx((s) => (s + 1) % SCENES.length); }
    }, DELAYS[step]);
    return () => clearTimeout(id);
  }, [step, inView, reduce]);

  const scene = SCENES[sceneIdx];
  const rows = buildRows(scene, cur, t);
  const youRank = 4 - cur;
  const pinRanks = [youRank, ...["c0", "c1", "c2"].map((id) => rows.find((r) => r.id === id).rank)];

  return (
    <div ref={ref} className="relative" role="img" aria-label={t("lp.rank.aria")} data-testid="rank-animation">
      <div className="pointer-events-none absolute -inset-6 rounded-[40px] opacity-40 blur-3xl" style={{ background: GRAD }} />
      <div className="relative rounded-[28px] p-px" style={{ background: "linear-gradient(160deg, rgba(139,92,246,0.45), rgba(255,255,255,0.6) 40%, rgba(236,72,153,0.35))" }}>
        <div className="rounded-[27px] bg-white/85 p-4 backdrop-blur-xl shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)]">
          <SearchBar key={scene.key} query={t(`lp.rank.scenarios.${scene.key}.query`)} t={t} />
          <MiniMap ranks={pinRanks} />
          <AnimatePresence mode="wait">
            <motion.div key={scene.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4 }}>
              <LayoutGroup id={scene.key}>
                <ul className="mt-2 divide-y divide-black/[0.04]" data-testid="rank-anim-list">
                  {rows.map((r) => <ResultRow key={r.id} row={r} isYou={r.id === "you"} sep={sep} t={t} />)}
                </ul>
              </LayoutGroup>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      <ReviewToast show={!reduce && step > 0 ? `${scene.key}-${step}` : null} scene={scene} t={t} />
      <RankPill rank={youRank} t={t} />
    </div>
  );
}

